import { Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { ArrowRight, Check, FileCheck2, SlidersHorizontal, Users } from "lucide-react";

import { trackOnce } from "@/lib/analytics/events";
import { IMPLEMENTATION_PACKAGES } from "@/lib/implementation-packages";

const BENEFITS = [
  {
    icon: FileCheck2,
    title: "See the evidence",
    text: "Review comparable sales, property sources and confidence before deciding what to offer.",
  },
  {
    icon: SlidersHorizontal,
    title: "Keep the assumptions yours",
    text: "Set your own ARV, repairs and offer rules, then review each deal consistently.",
  },
  {
    icon: Users,
    title: "Hand off a process, not a spreadsheet",
    text: "Give acquisitions reps a repeatable lead-to-offer workflow and a written SOP.",
  },
];

export function Landing() {
  useEffect(() => {
    trackOnce("landing_page_view");
    trackOnce("landing_view");
  }, []);

  return (
    <div>
      <section className="mx-auto max-w-6xl px-4 pb-14 pt-16 sm:px-6 sm:pt-24">
        <p className="text-xs font-semibold uppercase text-gold">
          ArosFlow · Done-with-you implementation
        </p>
        <h1 className="mt-5 max-w-4xl text-4xl font-semibold leading-tight sm:text-5xl">
          Turn your acquisition process into a repeatable lead-to-offer workflow.
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
          We configure ArosFlow around how your operation evaluates leads, calibrate real deals
          together and hand off a process your team can use. You remain in control of every
          assumption and offer.
        </p>
        <Link
          to="/implementation"
          hash="request"
          className="focus-key mt-8 inline-flex items-center gap-2 rounded-md gradient-gold px-6 py-3 text-sm font-semibold text-black"
        >
          Request an Implementation Call <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </section>

      <section className="border-y border-[var(--glass-border)] bg-card/40 py-12">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:grid-cols-3 sm:px-6">
          {BENEFITS.map(({ icon: Icon, title, text }) => (
            <div key={title}>
              <Icon className="h-5 w-5 text-gold" aria-hidden />
              <h2 className="mt-3 text-base font-semibold">{title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <h2 className="text-2xl font-semibold">Choose the level of implementation</h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          One-time professional service fees. Both engagements use ArosFlow capabilities available
          today.
        </p>
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          {IMPLEMENTATION_PACKAGES.map((offer) => (
            <article
              key={offer.id}
              className="rounded-md border border-[var(--glass-border)] bg-card p-6 sm:p-8"
            >
              <h3 className="text-lg font-semibold">{offer.name}</h3>
              <p className="mt-2 text-3xl font-semibold text-gold">
                {offer.price}
                <span className="ml-2 text-sm font-normal text-muted-foreground">one-time</span>
              </p>
              <ul className="mt-6 space-y-3">
                {offer.items.map((item) => (
                  <li key={item} className="flex gap-2 text-sm text-muted-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/implementation"
                hash="request"
                className="focus-key mt-7 inline-flex items-center gap-2 text-sm font-semibold text-gold hover:underline"
              >
                Request an Implementation Call <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </article>
          ))}
        </div>
        <p className="mt-8 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Software access, usage limits, and any ongoing charges are specified in your written
          proposal before you commit; implementation fees do not include lifetime access.
        </p>
        <p className="mt-3 max-w-3xl text-xs text-muted-foreground">
          Early-access features, team seats, blast messaging and call copilot are not implementation
          deliverables. No revenue or closing outcome is promised.
        </p>
      </section>
    </div>
  );
}
